import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as L from 'leaflet';

@Component({
  selector: 'app-google-map',
  imports: [],
  templateUrl: './google-map.html',
  styleUrl: './google-map.scss',
})
export class GoogleMap implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() latitude: number = 30.0444;
  @Input() longitude: number = 31.2357;
  @Input() propertyName: string = '';
  @Input() propertyAddress: string = '';
  @Input() propertyImage: string = '';

  // Unique id per instance to avoid conflicts when used multiple times on a page
  readonly mapId: string = `leaflet-map-${Math.random().toString(36).slice(2, 9)}`;

  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private isBrowser: boolean;
  private static isLeafletIconConfigured = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {}

  ngAfterViewInit() {
    if (this.isBrowser) {
      setTimeout(() => this.initMap(), 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.map && (changes['latitude'] || changes['longitude'])) {
      this.updateMapLocation();
    }
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initMap() {
    if (!this.isBrowser) return;

    this.configureLeafletMarkerIcon();

    const mapElement = document.getElementById(this.mapId);
    if (!mapElement) return;

    this.map = L.map(this.mapId).setView([this.latitude, this.longitude], 14);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(this.map);

    this.addMarker();
  }

  private updateMapLocation() {
    if (!this.map) return;
    this.map.setView([this.latitude, this.longitude], 14);
    if (this.marker) {
      this.map.removeLayer(this.marker);
    }
    this.addMarker();
  }

  private addMarker() {
    if (!this.map) return;

    const hasValidImage = this.propertyImage && this.propertyImage.trim() !== '';

    const popupContent = `
      <div style="width:120px; text-align:center; padding: 0;">
        ${
          hasValidImage
            ? `<img src="${this.propertyImage}" alt="${this.propertyName || 'Property'}"
                style="width:100%;height:70px;object-fit:cover;border-radius:6px;margin-bottom:6px;background:var(--color-bg-secondary);border:1px solid var(--color-border-subtle);"
                onerror="this.style.display='none'" />`
            : `<div style="width:100%;height:70px;background:linear-gradient(135deg, var(--color-primary), var(--color-primary-deep));
                border-radius:6px;margin-bottom:6px;display:flex;align-items:center;justify-content:center;color:white;box-shadow:inset 0 1px 0 rgba(255,255,255,0.1);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
               </div>`
        }
        <h4 style="margin:0 0 2px;font-weight:800;color:var(--color-text-primary);font-size:11px;letter-spacing:-0.02em;line-height:1.2;">
          ${this.propertyName || 'Property Location'}
        </h4>
        ${
          this.propertyAddress
            ? `<p style="font-size:9px;color:var(--color-text-muted);margin:0;line-height:1.3;font-weight:500;">${this.propertyAddress}</p>`
            : ''
        }
      </div>
    `;

    this.marker = L.marker([this.latitude, this.longitude])
      .addTo(this.map)
      .bindPopup(popupContent, { maxWidth: 220 })
      .openPopup();
  }

  private configureLeafletMarkerIcon() {
    if (GoogleMap.isLeafletIconConfigured) return;
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });
    GoogleMap.isLeafletIconConfigured = true;
  }
}
